import { NativeBiometric, BiometryType } from '@capgo/capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';
import { isNativeMobileApp } from './mobileApp';

const BIOMETRIC_SERVER = 'com.kcn.landlord';
const LANDLORD_EMAIL_KEY = 'kcn_landlord_last_email';

export type BiometricLoginStatus = {
  available: boolean;
  hasSavedAccount: boolean;
  label: string;
  icon: string;
  isFace: boolean;
  hint: string;
  savedEmail?: string | null;
};

function normalizeBiometryType(type: unknown): BiometryType {
  const value = Number(type);
  if (value === BiometryType.TOUCH_ID || value === BiometryType.FINGERPRINT) return value;
  if (value === BiometryType.FACE_ID || value === BiometryType.FACE_AUTHENTICATION) return value;
  if (value === BiometryType.IRIS_AUTHENTICATION || value === BiometryType.MULTIPLE) return value;
  return BiometryType.NONE;
}

function isFaceBiometry(type: BiometryType): boolean {
  return type === BiometryType.FACE_ID || type === BiometryType.FACE_AUTHENTICATION;
}

function biometricLabel(type: BiometryType): string {
  if (isFaceBiometry(type) || (type === BiometryType.NONE && Capacitor.getPlatform() === 'ios')) {
    return 'Đăng nhập bằng Face ID';
  }
  if (type === BiometryType.MULTIPLE) {
    return 'Đăng nhập bằng Face ID / vân tay';
  }
  return 'Đăng nhập bằng vân tay';
}

function biometricIcon(type: BiometryType): string {
  if (
    isFaceBiometry(type) ||
    type === BiometryType.MULTIPLE ||
    (type === BiometryType.NONE && Capacitor.getPlatform() === 'ios')
  ) {
    return 'fa-user-circle';
  }
  return 'fa-fingerprint';
}

function biometricHint(type: BiometryType, hasSavedAccount: boolean): string {
  if (hasSavedAccount) {
    if (isFaceBiometry(type) || type === BiometryType.MULTIPLE) {
      return 'Face ID dùng email đã lưu ở trên. Sau khi đăng xuất cần nhập mật khẩu lại.';
    }
    return 'Vân tay dùng email đã lưu ở trên. Sau khi đăng xuất cần nhập mật khẩu lại.';
  }
  if (isFaceBiometry(type) || type === BiometryType.MULTIPLE) {
    return 'Đăng nhập mật khẩu lần đầu để bật Face ID cho lần sau (trong cùng phiên, chưa đăng xuất).';
  }
  return 'Đăng nhập mật khẩu lần đầu để bật vân tay cho lần sau (trong cùng phiên, chưa đăng xuất).';
}

function emptyStatus(): BiometricLoginStatus {
  return {
    available: false,
    hasSavedAccount: false,
    label: 'Đăng nhập bằng vân tay',
    icon: 'fa-fingerprint',
    isFace: false,
    hint: '',
  };
}

export function getLastLandlordEmail(): string | null {
  try {
    const email = localStorage.getItem(LANDLORD_EMAIL_KEY);
    return email?.trim() || null;
  } catch {
    return null;
  }
}

export function saveLastLandlordEmail(email: string): void {
  const trimmed = email.trim();
  if (!trimmed) return;
  try {
    localStorage.setItem(LANDLORD_EMAIL_KEY, trimmed);
  } catch {
    // ignore
  }
}

export function clearLastLandlordEmail(): void {
  try {
    localStorage.removeItem(LANDLORD_EMAIL_KEY);
  } catch {
    // ignore
  }
}

export async function getBiometricLoginStatus(): Promise<BiometricLoginStatus> {
  try {
    if (!isNativeMobileApp()) return emptyStatus();
    const availability = await NativeBiometric.isAvailable();
    if (!availability.isAvailable && !availability.strongBiometryIsAvailable) {
      return emptyStatus();
    }
    const type = normalizeBiometryType(availability.biometryType);
    const saved = await NativeBiometric.isCredentialsSaved({ server: BIOMETRIC_SERVER });
    return {
      available: true,
      hasSavedAccount: saved.isSaved,
      label: biometricLabel(type),
      icon: biometricIcon(type),
      isFace: isFaceBiometry(type) || type === BiometryType.MULTIPLE
        || (type === BiometryType.NONE && Capacitor.getPlatform() === 'ios'),
      hint: biometricHint(type, saved.isSaved),
      savedEmail: getLastLandlordEmail(),
    };
  } catch {
    return emptyStatus();
  }
}

export async function saveBiometricCredentials(email: string, password: string): Promise<void> {
  saveLastLandlordEmail(email);
  try {
    if (!isNativeMobileApp()) return;
    const availability = await NativeBiometric.isAvailable();
    if (!availability.isAvailable && !availability.strongBiometryIsAvailable) return;
    await NativeBiometric.setCredentials({
      username: email.trim(),
      password,
      server: BIOMETRIC_SERVER,
    });
  } catch (err) {
    console.warn('[biometric] save skipped:', err);
  }
}

/** Xóa mật khẩu vân tay — sau đăng xuất phải nhập mật khẩu lại. Email vẫn giữ để hiển thị. */
export async function clearBiometricCredentials(): Promise<void> {
  try {
    if (!isNativeMobileApp()) return;
    await NativeBiometric.deleteCredentials({ server: BIOMETRIC_SERVER });
  } catch (err) {
    console.warn('[biometric] clear skipped:', err);
  }
}

export async function loginWithBiometrics(): Promise<{ email: string; password: string } | null> {
  try {
    if (!isNativeMobileApp()) return null;
    const availability = await NativeBiometric.isAvailable();
    if (!availability.isAvailable && !availability.strongBiometryIsAvailable) return null;

    const saved = await NativeBiometric.isCredentialsSaved({ server: BIOMETRIC_SERVER });
    if (!saved.isSaved) return null;

    const type = normalizeBiometryType(availability.biometryType);
    const isFace = isFaceBiometry(type) || type === BiometryType.MULTIPLE
      || (type === BiometryType.NONE && Capacitor.getPlatform() === 'ios');
    await NativeBiometric.verifyIdentity({
      reason: isFace ? 'Đăng nhập KCN Landlord bằng Face ID' : 'Đăng nhập KCN Landlord bằng vân tay',
      title: isFace ? 'Xác nhận Face ID' : 'Xác nhận vân tay',
      subtitle: isFace ? 'Dùng Face ID để đăng nhập' : 'Dùng vân tay để đăng nhập',
      description: isFace ? 'Nhìn vào camera TrueDepth' : 'Đặt ngón tay lên cảm biến',
      negativeButtonText: 'Dùng mật khẩu',
      fallbackTitle: 'Dùng mật khẩu',
      maxAttempts: 3,
    });

    const credentials = await NativeBiometric.getCredentials({ server: BIOMETRIC_SERVER });
    if (!credentials.username || !credentials.password) return null;
    return { email: credentials.username, password: credentials.password };
  } catch (err) {
    console.warn('[biometric] verify failed:', err);
    return null;
  }
}
